import WeeklyGrowthCard from '@/components/WeeklyGrowthCard';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Bell, CalendarRange, Loader2, Mail, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiGet, apiPost } from '@/lib/api';

const PANEL = 'rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden';
const HEAD = 'px-3.5 py-2 border-b flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-widest';

const RISK_TONE = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  orange: 'bg-amber-50 text-amber-900 border-amber-200',
  red: 'bg-rose-50 text-rose-800 border-rose-200',
};

/** Status update · Notifications · Urgent attention — the parent's three panels. */
export function ParentDashboardDialog({ open, onOpenChange, childId = '' }) {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [mailing, setMailing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const q = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    apiGet(`/premium/v1/parent/dashboard${q}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    apiGet(`/premium/v1/parent/digest${q}`).then(setDigest).catch(() => setDigest(null));
  }, [open, childId]);

  const emailDigest = async () => {
    setMailing(true);
    try {
      const res = await apiPost('/premium/v1/parent/digest/email', { child_id: childId || null });
      toast.success(`Sent to ${res.to}`);
    } catch (e) { toast.error(e?.message || 'Could not send that email'); }
    setMailing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="parent-dashboard-dialog">
        <DialogTitle className="font-heading text-lg font-bold text-slate-900">Parent dashboard</DialogTitle>
        <DialogDescription className="text-[13px] text-slate-500 font-body mb-1">
          {data?.student_name ? `How ${data.student_name}'s preparation is going` : 'How preparation is going'}
        </DialogDescription>

        {loading && <p className="py-6 text-center text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {!loading && data && (
          <div className="space-y-3">
            {data.student_id && <WeeklyGrowthCard childId={data.student_id} always canShare={false} />}
            <section className={PANEL} data-testid="dash-status-update">
              <p className={`${HEAD} border-indigo-100 bg-indigo-50/70 text-indigo-700`}>
                <TrendingUp className="w-3.5 h-3.5" /> Status update
              </p>
              <div className="p-3.5 space-y-2">
              {(data.status_update || []).length === 0 && (
                <p className="text-[13px] text-slate-600 font-body leading-relaxed">No daily plan yet. Map an exam portion and build the plan to see progress here.</p>
              )}
              {(data.status_update || []).map(s => (
                <div key={s.plan_id} className={`rounded-xl border p-3 ${RISK_TONE[s.risk] || RISK_TONE.green}`}
                  data-testid={`dash-status-${s.exam_id}`}>
                  <p className="font-heading text-[13px] font-semibold">{s.exam_name}</p>
                  <div className="h-1.5 rounded-full bg-white/70 mt-2 overflow-hidden">
                    <div className="h-full rounded-full bg-current opacity-70" style={{ width: `${s.progress_pct}%` }} />
                  </div>
                  <p className="text-[12px] font-body mt-1.5">
                    {s.items_done}/{s.items_total} done · about {Math.round(s.per_day_minutes / 6) / 10} hrs a day
                    {s.overdue_items > 0 && ` · ${s.overdue_items} pending from earlier days`}
                  </p>
                </div>
              ))}
              </div>
            </section>

            <section className={PANEL} data-testid="dash-notifications">
              <p className={`${HEAD} border-sky-100 bg-sky-50/70 text-sky-700`}>
                <Bell className="w-3.5 h-3.5" /> Notifications
              </p>
              <div className="p-3.5 space-y-2">
              {(data.notifications || []).length === 0 && (
                <p className="text-[13px] text-slate-600 font-body">Nothing new.</p>
              )}
              {(data.notifications || []).map(n => (
                <div key={n.notif_id} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <p className="font-heading text-[13px] font-semibold text-slate-800">{n.title}</p>
                  <p className="text-[12px] text-slate-600 font-body mt-0.5 leading-relaxed">{n.body}</p>
                </div>
              ))}
              </div>
            </section>

            <section className={PANEL} data-testid="dash-digest">
              <p className={`${HEAD} border-violet-100 bg-violet-50/70 text-violet-700`}>
                <CalendarRange className="w-3.5 h-3.5" /> Last week
              </p>
              <div className="p-3.5">
              {!digest && <p className="text-[13px] text-slate-400 font-body">Loading…</p>}
              {digest && (
                <div>
                  <p className="text-[13px] font-body text-slate-700 leading-relaxed">{digest.headline}</p>
                  <p className="text-[12px] text-slate-500 font-body mt-1">{digest.nudge}</p>
                  {digest.slipped?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5" data-testid="dash-digest-slipped">
                      {digest.slipped.map(s => (
                        <li key={s.exam_id} className="text-[12px] text-amber-700 font-body">
                          {s.exam_name}: {s.count} pending
                        </li>
                      ))}
                    </ul>
                  )}
                  {digest.email_available && (
                    <Button size="sm" variant="outline" className="h-8 text-[11px] mt-2.5" data-testid="dash-digest-email"
                      onClick={emailDigest} disabled={mailing}>
                      <Mail className="w-3 h-3 mr-1" /> {mailing ? 'Sending…' : 'Email this to me'}
                    </Button>
                  )}
                </div>
              )}
              </div>
            </section>

            <section className={PANEL} data-testid="dash-urgent">
              <p className={`${HEAD} border-rose-100 bg-rose-50/70 text-rose-700`}>
                <AlertTriangle className="w-3.5 h-3.5" /> Urgent attention
              </p>
              <div className="p-3.5 space-y-2">
              {(data.urgent_attention || []).length === 0 && (
                <p className="text-[13px] text-slate-600 font-body" data-testid="dash-urgent-empty">
                  Nothing needs you right now — the pace looks healthy.
                </p>
              )}
              {(data.urgent_attention || []).map((u, i) => (
                <div key={`${u.kind}-${i}`} className="rounded-xl border border-rose-200 bg-rose-50/70 p-3"
                  data-testid={`dash-urgent-${u.kind}`}>
                  <p className="font-heading text-[13px] font-semibold text-rose-800">{u.title}</p>
                  <p className="text-[12px] text-rose-700 font-body mt-0.5 leading-relaxed">{u.body}</p>
                </div>
              ))}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ParentDashboardDialog;
